/**
 * GstFetchResultPopup — GST notice-fetch result popup. Rendered once at app
 * level (App.js) and driven by GstFetchTrackerContext, so it shows on ANY
 * tenant page when a GST fetch completes — not just the GST notices list.
 *
 * SCOPE: GST notice fetch is only ever triggered from the tenant / NoticeAI
 * portal, so the result popup is suppressed on the Super-Admin (/admin) and
 * Partner-Admin (/partner-admin) portals — those are platform-ops surfaces
 * where this popup would be out of place.
 *
 * Super-admin-only extras (fetch method + "View Timing" button) are gated on
 * useAuth().isAdmin; normal users see only the success/failed counts.
 */
import { Modal, Button, Typography } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useGstFetchTracker } from "../context/GstFetchTrackerContext";
import { useAuth } from "../context/AuthContext";

const { Title, Text } = Typography;

// Each label names BOTH the engine (API vs Selenium) AND the surface where
// it ran — in-app (this server) vs Lambda (AWS cloud function). The backend
// sets `requests`/`selenium`/`selenium-fallback` for in-app runs and
// `lambda-api`/`lambda-selenium` (or the legacy bare `lambda`) for Lambda.
const METHOD_LABELS = {
  requests: "API · in-app (direct, fast)",
  api: "API · in-app (direct, fast)",
  selenium: "Selenium · in-app (browser)",
  "selenium-fallback": "Selenium · in-app (fallback)",
  "lambda-api": "API · Lambda (cloud, fast)",
  "lambda-selenium": "Selenium · Lambda (cloud)",
  lambda: "Lambda (cloud function)",
};

// Whether a method ran on AWS Lambda (vs in-app on this server).
const isLambdaMethod = (m) => typeof m === "string" && m.startsWith("lambda");

export default function GstFetchResultPopup() {
  const { showResult, progress, dismissResult } = useGstFetchTracker();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Tenant-portal only — never on the Super-Admin / Partner-Admin portals.
  const onPlatformPortal =
    pathname.startsWith("/admin") || pathname.startsWith("/partner-admin");
  if (onPlatformPortal) return null;

  if (!showResult || !progress) return null;

  return (
    <Modal
      open={showResult}
      onCancel={dismissResult}
      footer={[
        <Button key="ok" type="primary" onClick={dismissResult}>
          OK
        </Button>,
      ]}
      width={500}
      centered
      closable={false}
      title={null}
    >
      <div style={{ padding: "16px 0" }}>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <Title
            level={4}
            style={{
              color: progress.failed > 0 ? "#faad14" : "#52c41a",
              marginBottom: 8,
            }}
          >
            GST Notice Fetching Complete
          </Title>
          <Text type="secondary">
            All {progress.total} client(s) have been processed.
          </Text>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 32,
            marginBottom: 16,
            padding: 16,
            background: "#fafafa",
            borderRadius: 8,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: "bold", color: "#52c41a" }}>
              {progress.successful}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Successful
            </Text>
          </div>
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                fontSize: 28,
                fontWeight: "bold",
                color: progress.failed > 0 ? "#ff4d4f" : "#d9d9d9",
              }}
            >
              {progress.failed}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Failed
            </Text>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 28, fontWeight: "bold", color: "#1890ff" }}>
              {progress.total}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Total
            </Text>
          </div>
        </div>

        {/* SUPER-ADMIN ONLY: fetch method + timing button */}
        {isAdmin && (progress.fetchMethod || progress.hasTiming) && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "10px 14px",
              background: "#f0f5ff",
              border: "1px solid #d6e4ff",
              borderRadius: 8,
            }}
          >
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Fetch method
              </Text>
              <div
                style={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {METHOD_LABELS[progress.fetchMethod] ||
                  progress.fetchMethod ||
                  "-"}
                {progress.fetchMethod && (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "1px 8px",
                      borderRadius: 10,
                      color: isLambdaMethod(progress.fetchMethod)
                        ? "#722ed1"
                        : "#389e0d",
                      background: isLambdaMethod(progress.fetchMethod)
                        ? "#f9f0ff"
                        : "#f6ffed",
                      border: `1px solid ${
                        isLambdaMethod(progress.fetchMethod)
                          ? "#d3adf7"
                          : "#b7eb8f"
                      }`,
                    }}
                  >
                    {isLambdaMethod(progress.fetchMethod)
                      ? "Lambda"
                      : "In-app"}
                  </span>
                )}
              </div>
            </div>
            {progress.hasTiming && progress.logId && (
              <Button
                size="small"
                onClick={() => {
                  const id = progress.logId;
                  dismissResult();
                  navigate(`/gstin/fetch-timing/${id}`);
                }}
              >
                View Timing
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
